import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import KPICard from "@/components/admin/KPICard";
import { ArrowLeft, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { it as itLocale, enUS as enLocale, es as esLocale } from "date-fns/locale";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function AdminAnalytics() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    avgSubscriptionValue: 0,
    conversionRate: 0,
    churnRate: 0,
  });
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [subscriptionTypeData, setSubscriptionTypeData] = useState<any[]>([]);
  const [subscriptionStatusData, setSubscriptionStatusData] = useState<any[]>([]);

  const getDateLocale = () => {
    const currentLang = localStorage.getItem('i18nextLng') || 'it';
    switch (currentLang) {
      case 'en': return enLocale;
      case 'es': return esLocale;
      default: return itLocale;
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      // Load subscriptions data
      const { data: subscriptions } = await supabase
        .from("subscriptions")
        .select("amount_paid, status, created_at, product_type, product_duration");

      const activeSubscriptions = subscriptions?.filter(s => s.status === "active") || [];
      const totalRevenue = activeSubscriptions.reduce((sum, s) => sum + (Number(s.amount_paid) || 0), 0);
      const avgValue = activeSubscriptions.length > 0 ? totalRevenue / activeSubscriptions.length : 0;

      // Load users for conversion rate
      const { count: totalUsers } = await supabase
        .from("user_roles")
        .select("*", { count: "exact", head: true });

      const conversionRate = totalUsers && totalUsers > 0 
        ? (activeSubscriptions.length / totalUsers) * 100 
        : 0;

      setMetrics({
        totalRevenue,
        avgSubscriptionValue: avgValue,
        conversionRate: Math.round(conversionRate * 10) / 10,
        churnRate: 5.2, // Mock data
      });

      // Prepare revenue by month data (last 6 months)
      const monthsData: any[] = [];
      for (let i = 5; i >= 0; i--) {
        const monthDate = subMonths(new Date(), i);
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);
        
        const monthSubs = subscriptions?.filter(s => {
          const subDate = new Date(s.created_at);
          return subDate >= monthStart && subDate <= monthEnd;
        }) || [];
        
        const monthRevenue = monthSubs.reduce((sum, s) => sum + (Number(s.amount_paid) || 0), 0);
        
        monthsData.push({
          month: format(monthDate, "MMM yyyy", { locale: getDateLocale() }),
          revenue: monthRevenue,
          subscriptions: monthSubs.length
        });
      }
      setRevenueData(monthsData);

      // Prepare subscription by type data
      const typeMap = new Map<string, number>();
      subscriptions?.forEach(s => {
        const type = s.product_type || "unknown";
        typeMap.set(type, (typeMap.get(type) || 0) + 1);
      });
      
      const typeData = Array.from(typeMap.entries()).map(([type, count]) => ({
        type: type.toUpperCase(),
        count
      }));
      setSubscriptionTypeData(typeData);

      // Prepare subscription by status data
      const statusMap = new Map<string, number>();
      subscriptions?.forEach(s => {
        const status = s.status || "unknown";
        statusMap.set(status, (statusMap.get(status) || 0) + 1);
      });
      
      const statusData = Array.from(statusMap.entries()).map(([status, count]) => ({
        status: status.toUpperCase(),
        count
      }));
      setSubscriptionStatusData(statusData);

    } catch (error) {
      console.error(t("admin.analytics.messages.loadError"), error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-surface rounded w-1/4"></div>
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-surface rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.analytics.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.analytics.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <BarChart3 className="w-10 h-10 text-orange-600" />
              {t("admin.analytics.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.analytics.subtitle")}</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <KPICard
            title={t("admin.totalRevenue")}
            value={`€${metrics.totalRevenue.toFixed(2)}`}
          />
          <KPICard
            title={t("admin.avgSubscriptionValue")}
            value={`€${metrics.avgSubscriptionValue.toFixed(2)}`}
          />
          <KPICard
            title={t("admin.conversionRate")}
            value={`${metrics.conversionRate}%`}
          />
          <KPICard
            title={t("admin.churnRate")}
            value={`${metrics.churnRate}%`}
            trend="down"
            inverse
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Revenue Chart */}
          <BrutalCard>
            <h2 className="font-heading text-2xl font-bold mb-6 uppercase">
              {t("admin.analytics.charts.monthlyRevenue")}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeWidth={3} stroke="hsl(var(--dark))" />
                <XAxis 
                  dataKey="month" 
                  stroke="hsl(var(--dark))"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                />
                <YAxis 
                  stroke="hsl(var(--dark))"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                />
                <Tooltip 
                  contentStyle={{
                    border: '3px solid hsl(var(--dark))',
                    borderRadius: 0,
                    boxShadow: '4px 4px 0px hsl(var(--dark))',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 'bold'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={4}
                  dot={{ fill: 'hsl(var(--primary))', strokeWidth: 3, r: 6, stroke: 'hsl(var(--dark))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </BrutalCard>

          {/* Subscription Types Chart */}
          <BrutalCard>
            <h2 className="font-heading text-2xl font-bold mb-6 uppercase">
              {t("admin.analytics.charts.subscriptionsByType")}
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={subscriptionTypeData}>
                <CartesianGrid strokeWidth={3} stroke="hsl(var(--dark))" />
                <XAxis 
                  dataKey="type" 
                  stroke="hsl(var(--dark))"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                />
                <YAxis 
                  stroke="hsl(var(--dark))"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 'bold' }}
                />
                <Tooltip 
                  contentStyle={{
                    border: '3px solid hsl(var(--dark))',
                    borderRadius: 0,
                    boxShadow: '4px 4px 0px hsl(var(--dark))',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 'bold'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  fill="hsl(var(--primary))" 
                  stroke="hsl(var(--dark))"
                  strokeWidth={3}
                />
              </BarChart>
            </ResponsiveContainer>
          </BrutalCard>
        </div>

        {/* Status Pie Chart */}
        <BrutalCard>
          <h2 className="font-heading text-2xl font-bold mb-6 uppercase">
            {t("admin.analytics.charts.subscriptionStatus")}
          </h2>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={subscriptionStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ status, count }) => `${status}: ${count}`}
                outerRadius={120}
                dataKey="count"
                stroke="hsl(var(--dark))"
                strokeWidth={3}
              >
                {subscriptionStatusData.map((entry, index) => {
                  const colors = [
                    'hsl(var(--primary))',
                    'hsl(var(--accent))',
                    'hsl(var(--muted))',
                    'hsl(var(--surface))',
                  ];
                  return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                })}
              </Pie>
              <Tooltip 
                contentStyle={{
                  border: '3px solid hsl(var(--dark))',
                  borderRadius: 0,
                  boxShadow: '4px 4px 0px hsl(var(--dark))',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 'bold'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </BrutalCard>
      </div>
    </AdminLayout>
  );
}
